// Localização: frontend/src/pages/Assinatura/GerenciarAssinaturaPage.jsx
// Página para gerenciar assinatura atual

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
    CreditCard, 
    Calendar, 
    AlertCircle, 
    CheckCircle, 
    XCircle, 
    Loader2,
    ArrowLeft,
    Receipt,
    Settings,
    Shield,
    Sparkles,
    ArrowRight
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { cancelAssinatura, getAssinatura } from '../../api/assinaturaApi';
import { cancelSubscription as cancelStripeSubscription } from '../../api/stripeApi';
import SubscriptionCard from '../../components/Stripe/SubscriptionCard';
import PrimaryButton from '../../components/Common/PrimaryButton';
import ModalCancelamentoAssinatura from '../../components/Assinatura/ModalCancelamentoAssinatura';

const GerenciarAssinaturaPage = () => {
    const { subscription, fetchSubscription, user } = useAuth();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [cancelando, setCancelando] = useState(false);
    const [showModalCancelamento, setShowModalCancelamento] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        console.log('[GerenciarAssinaturaPage] 🔍 useEffect executado - subscription:', subscription?.id || 'null');
        
        const carregarAssinatura = async () => {
            try {
                setLoading(true);
                setError(null);
                console.log('[GerenciarAssinaturaPage] 📥 Carregando assinatura...');
                // Usar getAssinatura diretamente para evitar dependência circular
                const response = await getAssinatura();
                if (response.success && response.data) {
                    console.log('[GerenciarAssinaturaPage] ✅ Assinatura encontrada');
                    // Atualizar assinatura no contexto
                    await fetchSubscription();
                }
            } catch (err) {
                console.error('[GerenciarAssinatura] ❌ Erro ao carregar assinatura:', err);
                setError('Erro ao carregar dados da assinatura');
            } finally {
                setLoading(false);
            }
        };

        // Só carregar se não tiver subscription no contexto ainda
        if (!subscription) {
            console.log('[GerenciarAssinaturaPage] 📋 Sem subscription no contexto, carregando...');
            carregarAssinatura();
        } else {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // Executa apenas uma vez ao montar o componente

    const handleCancelar = async (motivoCancelamento) => {
        if (!subscription) return;

        try {
            setCancelando(true);
            setError(null);

            console.log('[GerenciarAssinatura] Motivo do cancelamento:', motivoCancelamento);

            // Se tiver stripe_subscription_id e não for trial, cancelar no Stripe também
            if (subscription.stripe_subscription_id && !subscription.stripe_subscription_id.startsWith('TRIAL_')) {
                try {
                    await cancelStripeSubscription(subscription.stripe_subscription_id, false);
                    console.log('[GerenciarAssinatura] Assinatura cancelada no Stripe');
                } catch (stripeError) {
                    console.warn('[GerenciarAssinatura] Erro ao cancelar no Stripe (continuando):', stripeError);
                }
            }

            const response = await cancelAssinatura(subscription.id);

            if (response.success) {
                // Atualizar assinatura no contexto
                await fetchSubscription();
                setShowModalCancelamento(false);
                
                const mensagem = subscription.status === 'PENDENTE'
                    ? 'Assinatura pendente cancelada com sucesso. Você pode criar uma nova assinatura quando desejar.'
                    : 'Assinatura cancelada com sucesso. Ela permanecerá ativa até o final do período pago.';
                
                alert(mensagem);
                
                // Se era pendente, redirecionar para página de planos
                if (subscription.status === 'PENDENTE') {
                    setTimeout(() => {
                        navigate('/pricing');
                    }, 1500);
                }
            } else {
                throw new Error(response.message || 'Erro ao cancelar assinatura');
            }
        } catch (err) {
            console.error('[GerenciarAssinatura] Erro ao cancelar:', err);
            setError(err.response?.data?.message || 'Erro ao cancelar assinatura. Tente novamente.');
        } finally {
            setCancelando(false);
        }
    };

    const handleAtualizarPlano = () => {
        navigate('/pricing?upgrade=true');
    };

    const handleVerPlanos = () => {
        // Se for trial, adicionar parâmetro para facilitar conversão
        if (subscription?.is_trial) {
            navigate('/pricing?convert_trial=true');
        } else {
            navigate('/pricing?upgrade=true');
        }
    };

    // Função para calcular dias restantes do trial
    const calcularDiasRestantes = () => {
        if (!subscription?.is_trial || !subscription?.data_expiracao) return null;
        
        const hoje = new Date();
        hoje.setHours(0, 0, 0, 0);
        const expiracao = new Date(subscription.data_expiracao);
        expiracao.setHours(0, 0, 0, 0);
        
        const diffTime = expiracao - hoje;
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        
        return diffDays;
    };

    // Função para obter mensagem de notificação baseada nos dias restantes
    const getTrialNotification = (diasRestantes) => {
        if (diasRestantes === null) return null;
        
        if (diasRestantes < 0) {
            return {
                type: 'error',
                title: 'Trial Expirado',
                message: `Seu trial Premium expirou há ${Math.abs(diasRestantes)} ${Math.abs(diasRestantes) === 1 ? 'dia' : 'dias'}. Assine agora para continuar aproveitando todos os recursos Premium!`,
                action: 'Assinar Agora'
            };
        } else if (diasRestantes === 0) {
            return {
                type: 'error',
                title: 'Trial Expira Hoje!',
                message: 'Seu trial Premium expira hoje! Não perca acesso aos recursos Premium. Assine agora!',
                action: 'Assinar Agora'
            };
        } else if (diasRestantes <= 3) {
            return {
                type: 'warning',
                title: 'Trial Expirando em Breve',
                message: `Seu trial Premium expira em ${diasRestantes} ${diasRestantes === 1 ? 'dia' : 'dias'}. Assine agora e garanta acesso contínuo!`,
                action: 'Assinar Agora'
            };
        } else if (diasRestantes <= 7) {
            return {
                type: 'info',
                title: 'Trial Expirando em Breve',
                message: `Você tem ${diasRestantes} dias restantes no trial Premium. Aproveite para explorar todos os recursos antes de assinar!`,
                action: 'Ver Planos'
            };
        } else {
            return {
                type: 'success',
                title: 'Trial Ativo',
                message: `Você tem ${diasRestantes} dias restantes no trial Premium. Explore todos os recursos disponíveis!`,
                action: null
            };
        }
    };

    const getStatusBadge = (status) => {
        const badges = {
            ATIVO: {
                icon: CheckCircle,
                color: 'bg-green-100 text-green-700 border-green-200',
                label: 'Ativo'
            },
            TRIAL: {
                icon: CheckCircle,
                color: 'bg-purple-100 text-purple-700 border-purple-200',
                label: 'Trial Ativo'
            },
            PENDENTE: {
                icon: Loader2,
                color: 'bg-yellow-100 text-yellow-700 border-yellow-200',
                label: 'Pendente'
            },
            CANCELADO: {
                icon: XCircle,
                color: 'bg-red-100 text-red-700 border-red-200',
                label: 'Cancelado'
            },
            ATRASADO: {
                icon: AlertCircle,
                color: 'bg-orange-100 text-orange-700 border-orange-200',
                label: 'Atrasado'
            }
        };

        const badge = badges[status] || badges.PENDENTE;
        const Icon = badge.icon;

        return (
            <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${badge.color}`}>
                <Icon className="h-4 w-4 mr-1" />
                {badge.label}
            </span>
        );
    };

    const isExpirada = () => {
        if (!subscription?.data_expiracao) return false;
        return new Date(subscription.data_expiracao) < new Date();
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <Loader2 className="h-12 w-12 animate-spin text-higeia-dark mx-auto mb-4" />
                    <p className="text-gray-600">Carregando informações da assinatura...</p>
                </div>
            </div>
        );
    }

    if (!subscription) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
                <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center">
                    <CreditCard className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                    <h1 className="text-2xl font-bold text-gray-900 mb-2">
                        Nenhuma Assinatura Ativa
                    </h1>
                    <p className="text-gray-600 mb-6">
                        Você ainda não possui uma assinatura ativa. Escolha um plano para começar.
                    </p>
                    <PrimaryButton
                        onClick={() => navigate('/pricing')}
                        className="w-full bg-higeia-dark hover:bg-higeia-medium"
                    >
                        Ver Planos
                    </PrimaryButton>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 py-8 px-4 sm:px-6 lg:px-8">
            <div className="max-w-5xl mx-auto">
                {/* Header Melhorado */}
                <div className="mb-8">
                    <button
                        onClick={() => navigate(-1)}
                        className="flex items-center text-gray-600 hover:text-gray-900 mb-6 transition-colors group"
                    >
                        <ArrowLeft className="h-4 w-4 mr-2 group-hover:-translate-x-1 transition-transform" />
                        <span>Voltar</span>
                    </button>
                    <div className="flex items-center gap-4 mb-2">
                        <div className="p-3 bg-higeia-dark rounded-xl shadow-lg">
                            <Settings className="h-8 w-8 text-white" />
                        </div>
                        <div>
                            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900">
                                Gerenciar Assinatura
                            </h1>
                            <p className="text-gray-600 mt-1">
                                Gerencie sua assinatura e configurações de pagamento
                            </p>
                        </div>
                    </div>
                </div>

                {/* Notificação de Trial */}
                {subscription?.is_trial && (() => {
                    const diasRestantes = calcularDiasRestantes();
                    const notificacao = getTrialNotification(diasRestantes);
                    
                    if (!notificacao) return null;
                    
                    const bgColors = {
                        error: 'bg-red-50 border-red-200',
                        warning: 'bg-orange-50 border-orange-200',
                        info: 'bg-blue-50 border-blue-200',
                        success: 'bg-green-50 border-green-200'
                    };
                    
                    const textColors = {
                        error: 'text-red-900',
                        warning: 'text-orange-900',
                        info: 'text-blue-900',
                        success: 'text-green-900'
                    };
                    
                    const iconColors = {
                        error: 'text-red-600',
                        warning: 'text-orange-600',
                        info: 'text-blue-600',
                        success: 'text-green-600'
                    };
                    
                    return (
                        <div className={`${bgColors[notificacao.type]} border rounded-lg p-4 mb-6`}>
                            <div className="flex items-start justify-between">
                                <div className="flex items-start flex-1">
                                    <AlertCircle className={`h-5 w-5 ${iconColors[notificacao.type]} mr-2 mt-0.5 flex-shrink-0`} />
                                    <div className="flex-1">
                                        <h3 className={`font-semibold ${textColors[notificacao.type]} mb-1`}>
                                            {notificacao.title}
                                        </h3>
                                        <p className={`text-sm ${textColors[notificacao.type]} mb-3`}>
                                            {notificacao.message}
                                        </p>
                                        {notificacao.action && (
                                            <PrimaryButton
                                                onClick={() => navigate('/pricing')}
                                                className="mt-2"
                                            >
                                                {notificacao.action}
                                            </PrimaryButton>
                                        )}
                                    </div>
                                </div>
                                {diasRestantes !== null && (
                                    <div className={`ml-4 text-right ${textColors[notificacao.type]}`}>
                                        <div className="text-3xl font-bold">
                                            {diasRestantes < 0 ? `-${Math.abs(diasRestantes)}` : diasRestantes}
                                        </div>
                                        <div className="text-xs font-medium">
                                            {diasRestantes < 0 ? 'dias expirados' : diasRestantes === 1 ? 'dia restante' : 'dias restantes'}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                })()}

                {/* Aviso de Expiração (para assinaturas não-trial) */}
                {!subscription?.is_trial && isExpirada() && (
                    <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-6">
                        <div className="flex items-start">
                            <AlertCircle className="h-5 w-5 text-orange-600 mr-2 mt-0.5" />
                            <div>
                                <h3 className="font-semibold text-orange-900 mb-1">
                                    Assinatura Expirada
                                </h3>
                                <p className="text-sm text-orange-700">
                                    Sua assinatura expirou. Renove para continuar usando todos os recursos.
                                </p>
                            </div>
                        </div>
                    </div>
                )}

                {/* Erro */}
                {error && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                        <p className="text-red-700 text-sm">{error}</p>
                    </div>
                )}

                {/* Card Principal - Usando SubscriptionCard */}
                <div className="mb-8">
                    <SubscriptionCard
                        subscription={subscription}
                        onCancel={() => setShowModalCancelamento(true)}
                        onUpdate={handleAtualizarPlano}
                    />
                </div>

                {/* Card de Acesso aos Planos - Destacado para conversão de trial */}
                {(subscription?.is_trial || subscription?.status === 'ATIVO') && (
                    <div className="mb-8 bg-gradient-to-r from-higeia-dark to-higeia-medium rounded-xl shadow-lg border border-higeia-dark/20 overflow-hidden">
                        <div className="p-6 md:p-8">
                            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                                <div className="flex items-start gap-4 flex-1">
                                    <div className="p-3 bg-white/20 rounded-lg backdrop-blur-sm">
                                        <Sparkles className="h-6 w-6 text-white" />
                                    </div>
                                    <div className="flex-1">
                                        <h3 className="text-xl font-bold text-white mb-2">
                                            {subscription?.is_trial 
                                                ? 'Converta seu Trial em um Plano Efetivo' 
                                                : 'Explore Nossos Planos Disponíveis'}
                                        </h3>
                                        <p className="text-white/90 text-sm md:text-base">
                                            {subscription?.is_trial 
                                                ? 'Seu período de trial está ativo. Escolha um plano para continuar aproveitando todos os recursos após o término do trial.'
                                                : 'Compare nossos planos e escolha o que melhor se adequa às suas necessidades. Você pode alterar seu plano a qualquer momento.'}
                                        </p>
                                        {subscription?.is_trial && calcularDiasRestantes() !== null && (
                                            <p className="text-white/80 text-xs mt-2">
                                                {calcularDiasRestantes() > 0 
                                                    ? `${calcularDiasRestantes()} ${calcularDiasRestantes() === 1 ? 'dia restante' : 'dias restantes'} no trial`
                                                    : 'Trial expirado - Assine agora para continuar'}
                                            </p>
                                        )}
                                    </div>
                                </div>
                                <div className="flex-shrink-0">
                                    <PrimaryButton
                                        onClick={handleVerPlanos}
                                        className="bg-white text-higeia-dark hover:bg-gray-100 font-semibold px-6 py-3 flex items-center gap-2 shadow-lg hover:shadow-xl transition-all"
                                    >
                                        <span>{subscription?.is_trial ? 'Assinar Plano Agora' : 'Ver Planos'}</span>
                                        <ArrowRight className="h-4 w-4" />
                                    </PrimaryButton>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Card de Acesso aos Planos - Para outros status */}
                {subscription?.status !== 'ATIVO' && !subscription?.is_trial && (
                    <div className="mb-8 bg-white rounded-xl shadow-lg border border-gray-200 p-6">
                        <div className="flex items-start gap-4">
                            <div className="p-3 bg-blue-100 rounded-lg">
                                <Sparkles className="h-6 w-6 text-blue-600" />
                            </div>
                            <div className="flex-1">
                                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                                    Explore Nossos Planos
                                </h3>
                                <p className="text-gray-600 text-sm mb-4">
                                    Veja todos os planos disponíveis e escolha o que melhor se adequa às suas necessidades.
                                </p>
                                <PrimaryButton
                                    onClick={handleVerPlanos}
                                    className="bg-higeia-dark hover:bg-higeia-medium text-white"
                                >
                                    Ver Planos Disponíveis
                                </PrimaryButton>
                            </div>
                        </div>
                    </div>
                )}

                {/* Informações Técnicas (apenas se necessário) */}
                {subscription.stripe_subscription_id && !subscription.stripe_subscription_id.startsWith('TRIAL_') && (
                    <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-100 mb-8">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="p-2 bg-blue-100 rounded-lg">
                                <Shield className="h-5 w-5 text-blue-600" />
                            </div>
                            <h3 className="text-lg font-semibold text-gray-900">Informações Técnicas</h3>
                        </div>
                        <div className="flex items-start gap-3">
                            <CreditCard className="h-5 w-5 text-gray-400 mt-0.5 flex-shrink-0" />
                            <div className="flex-1 min-w-0">
                                <p className="text-sm text-gray-500 mb-1">ID Stripe</p>
                                <p className="font-mono text-xs text-gray-700 break-all bg-gray-50 p-2 rounded">
                                    {subscription.stripe_subscription_id}
                                </p>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Modal de Cancelamento */}
            <ModalCancelamentoAssinatura
                isOpen={showModalCancelamento}
                onClose={() => setShowModalCancelamento(false)}
                onConfirm={handleCancelar}
                isLoading={cancelando}
                isPending={subscription?.status === 'PENDENTE'}
            />
        </div>
    );
};

export default GerenciarAssinaturaPage;

